/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useRef, useState } from 'react';
import { canUseDOM } from '@/app/common/utils/canUseDOM';

type UseOnScreenWithRefCallback = {
  defaultValue?: boolean;
  options?: any;
};

type UseOnScreenWithRefCallbackReturn = [setRef: (node: Element) => void, domNode: Element | null, isVisible: boolean];

const useOnScreenWithRefCallback = ({
  defaultValue,
  options = {},
}: UseOnScreenWithRefCallback): UseOnScreenWithRefCallbackReturn => {
  const ref = useRef<Element | null>(null);
  const [isVisible, setIntersecting] = useState<boolean>(defaultValue || false);
  const [domNode, setDomNode] = useState<Element | null>(null);
  let observer: IntersectionObserver;

  const setRef = useCallback((node: Element) => {
    if (ref.current) {
      // Make sure to clean up any events/references added to the last instance
      observer?.disconnect();
    }

    if (node) {
      setDomNode(node);
      if (canUseDOM && 'IntersectionObserver' in window) {
        observer = new IntersectionObserver(([entry]) => setIntersecting(entry.isIntersecting), options);
      }

      if (observer && node) observer.observe(node);
    }

    // Save a reference to the node
    ref.current = node;
  }, []);

  return [setRef, domNode, isVisible];
};

export default useOnScreenWithRefCallback;
